import { DatasetDropdown } from "components/basic/dropdowns/dataset/DatasetDropdown";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch, InputSwitchChangeEvent } from "primereact/inputswitch";
import React from "react";
import { T } from "services/lang/LanguageService";
import { IDataSetConfig } from "../../common/api/interfaces/interfaces";

interface AddDatasetToInputOutputTableProps {
  addDatasetDialogVisible: boolean;
  setAddDatasetDialogVisible: (visible: boolean) => void;
  newDataset: IDataSetConfig;
  setNewDataset: React.Dispatch<React.SetStateAction<IDataSetConfig>>;
  handleConfirmAddDataset: () => void;
}

export const TYPE_OPTIONS = [{ value: "NUMBER" }, { value: "BOOLEAN" }, { value: "ENUM" }];

const AddDatasetToInputOutputTable = ({
  addDatasetDialogVisible,
  setAddDatasetDialogVisible,
  newDataset,
  setNewDataset,
  handleConfirmAddDataset,
}: AddDatasetToInputOutputTableProps) => {
  const rangeEditor = () => {
    const currentRange = newDataset.valueRange || [undefined, undefined];

    if (
      newDataset.type?.toUpperCase() === "BOOLEAN" ||
      newDataset.type?.toUpperCase() === "ENUM" ||
      (newDataset.type === undefined && newDataset.identifier === "")
    ) {
      return null;
    }

    return (
      <div className="flex flex-column gap-2">
        <label htmlFor="range">{T("word.range")}</label>
        <div className="flex flex-row gap-2">
          <InputNumber
            className="w-6"
            value={currentRange[0]}
            maxFractionDigits={2}
            placeholder="MIN"
            onValueChange={e => {
              const newRange: Array<number | undefined | null> = [e.value, currentRange[1]];
              setNewDataset(prev => ({ ...prev, valueRange: newRange as number[] }));
            }}
          />
          <InputNumber
            className="w-6"
            maxFractionDigits={2}
            value={currentRange[1]}
            placeholder="MAX"
            onValueChange={e => {
              const newRange: Array<number | undefined | null> = [currentRange[0], e.value];
              setNewDataset(prev => ({ ...prev, valueRange: newRange as number[] }));
            }}
          />
        </div>
      </div>
    );
  };

  const handleDatasetChange = (e: InputSwitchChangeEvent | DropdownChangeEvent) =>
    setNewDataset(prev => ({ ...prev, [e.target.id]: e.value }));

  const footerTemplate = () => {
    return (
      <div className="flex flex-row justify-content-end gap-2">
        <Button
          label={T("action.cancel")}
          icon="pi pi-times"
          className="component-editor-button icon-red"
          onClick={() => setAddDatasetDialogVisible(false)}
        />
        <Button
          label={T("action.add")}
          icon="pi pi-plus"
          onClick={handleConfirmAddDataset}
          className="component-editor-button icon-green"
          disabled={newDataset.identifier === ""}
        />
      </div>
    );
  };

  return (
    <Dialog
      header={T("action.add-dataset")}
      visible={addDatasetDialogVisible}
      onHide={() => setAddDatasetDialogVisible(false)}
      style={{ width: "400px" }}
      footer={footerTemplate}
    >
      <div className="flex flex-column gap-3 w-12">
        <div className="w-12 flex flex-row gap-2">
          <div className="flex flex-column gap-2 w-10">
            <label className="block mb-1">{T("word.dataset")}</label>
            <DatasetDropdown
              value={newDataset.identifier}
              onChange={value => setNewDataset(prev => ({ ...prev, identifier: value ?? "" }))}
            />
          </div>
          <div className="flex flex-column gap-2 w-2">
            <label>{T("word.editable")}</label>
            <InputSwitch
              id="editable"
              checked={newDataset.editable ?? false}
              className="mt-1"
              onChange={handleDatasetChange}
            />
          </div>
        </div>
        <div className="flex flex-row gap-2">
          <div className="flex flex-column gap-2 w-6">
            <label className="block mb-1">{T("word.type")}</label>
            <Dropdown
              id="type"
              value={newDataset.type}
              options={TYPE_OPTIONS}
              optionLabel="value"
              onChange={handleDatasetChange}
              placeholder={T("action.select-type")}
            />
          </div>
          <div className="flex flex-column gap-2 w-6">
            <label className="block mb-1">{T("word.decimal")}</label>
            <InputNumber
              id="decimal"
              value={newDataset.decimalPlaces}
              onValueChange={e => setNewDataset({ ...newDataset, decimalPlaces: e.value ?? 0 })}
            />
          </div>
        </div>
        {rangeEditor()}
      </div>
    </Dialog>
  );
};

export default AddDatasetToInputOutputTable;
